import React, { useEffect, useState, useCallback } from "react";
import { Briefcase, MapPin, Building2, Star, Plus, Loader2, Trash2, Search, Mail, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo } from "@/lib/api";
import { useAuth, useRequireAuth } from "@/context/AuthContext";
import { MedLyncVerifiedBadge } from "@/components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const TYPES = ["All", "Job", "Tender", "Equipment", "Service"];
const TYPE_COLOR = {
  Job: "bg-orange-50 text-orange-700",
  Tender: "bg-indigo-50 text-indigo-700",
  Equipment: "bg-emerald-50 text-emerald-700",
  Service: "bg-sky-50 text-sky-700",
};

function CreateOppDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", type: "Job", organization: "", location: "Ahmedabad", contact: "", compensation: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.title.trim() || !f.description.trim()) { toast.info("Add a title and description"); return; }
    setBusy(true);
    try {
      await api.post("/opportunities", { ...f, compensation: f.compensation || null, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Listing posted"); onOpenChange(false); onCreated();
      setF({ title: "", description: "", type: "Job", organization: "", location: "Ahmedabad", contact: "", compensation: "", tags: "" });
    } catch { toast.error("Could not post"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="create-opportunity-modal">
        <DialogHeader><DialogTitle className="font-heading">Post a Listing</DialogTitle><DialogDescription className="sr-only">Create a marketplace or opportunity listing.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Select value={f.type} onValueChange={(v) => setF((p) => ({ ...p, type: v }))}>
            <SelectTrigger data-testid="opp-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.filter((t) => t !== "All").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="opp-title-input" value={f.title} onChange={set("title")} placeholder="Title" />
          <Textarea data-testid="opp-desc-input" value={f.description} onChange={set("description")} placeholder="Description" className="min-h-[90px]" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.organization} onChange={set("organization")} placeholder="Organization" />
            <Input value={f.location} onChange={set("location")} placeholder="Location" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.compensation} onChange={set("compensation")} placeholder="Compensation / Price" />
            <Input value={f.contact} onChange={set("contact")} placeholder="Contact" />
          </div>
          <Input value={f.tags} onChange={set("tags")} placeholder="Tags, comma separated" />
          <Button data-testid="opp-submit-button" onClick={submit} disabled={busy} className="w-full bg-medorange hover:bg-medorange-hover">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post listing"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Opportunities() {
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const [type, setType] = useState("All");
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (type !== "All") params.type = type;
    if (q) params.q = q;
    const { data } = await api.get("/opportunities", { params });
    setList(data);
  }, [type, q]);
  useEffect(() => { load(); }, [load]);

  const interest = (o) => requireAuth(async () => {
    const { data } = await api.post(`/opportunities/${o.id}/interest`);
    setList((l) => l.map((x) => (x.id === o.id ? data : x)));
    toast.success(data.interested ? "Saved to your interests" : "Removed");
  });
  const verify = async (o) => { const { data } = await api.patch(`/opportunities/${o.id}/verify`); setList((l) => l.map((x) => (x.id === o.id ? { ...x, medlync_verified: data.medlync_verified } : x))); };
  const remove = async (o) => { await api.delete(`/opportunities/${o.id}`); toast.success("Deleted"); load(); };

  const isAdmin = user && user.role === "admin";

  return (
    <div className="px-4 py-4 pb-8" data-testid="opportunities-page">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-medorange-light text-medorange"><Briefcase className="h-5 w-5" /></span>
        <div className="flex-1"><h1 className="font-heading text-xl font-extrabold text-slate-900">Marketplace</h1><p className="text-xs text-slate-500">Jobs, tenders, equipment & services</p></div>
        <Button data-testid="add-opportunity-button" onClick={() => requireAuth(() => setOpen(true))} size="sm" className="gap-1 bg-navy hover:bg-navy-hover"><Plus className="h-4 w-4" /> Post</Button>
      </div>

      <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto">
        {TYPES.map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`opp-filter-${t.toLowerCase()}`} className={cn("shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors", type === t ? "bg-medorange text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100")}>{t}</button>
        ))}
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        <Search className="h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search listings…" className="flex-1 bg-transparent text-sm outline-none" />
      </div>

      <div className="space-y-4">
        {list === null && [1, 2].map((i) => <Skeleton key={i} className="h-36 w-full rounded-2xl" />)}
        {list && list.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No listings found.</p>}
        {list && list.map((o) => (
          <div key={o.id} data-testid="opportunity-card" className="ml-rise rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase", TYPE_COLOR[o.type] || "bg-slate-100 text-slate-700")}>{o.type}</span>
                {o.medlync_verified && <MedLyncVerifiedBadge compact />}
              </div>
              {(isAdmin || (user && user.id === o.posted_by)) && <button onClick={() => remove(o)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <h3 className="mt-2 font-heading text-base font-bold leading-snug text-slate-900">{o.title}</h3>
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{o.description}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              {o.organization && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {o.organization}</span>}
              {o.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.location}</span>}
              {o.compensation && <span className="font-semibold text-medorange">{o.compensation}</span>}
            </div>
            {o.tags && o.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">{o.tags.map((t) => <span key={t} className="inline-flex items-center gap-0.5 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"><Tag className="h-2.5 w-2.5" />{t}</span>)}</div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <Button onClick={() => interest(o)} data-testid={`opp-interest-${o.id}`} className={cn("flex-1", o.interested ? "bg-orange-50 text-medorange hover:bg-orange-100" : "bg-medorange text-white hover:bg-medorange-hover")}>
                <Star className={cn("mr-1 h-4 w-4", o.interested && "fill-medorange")} /> {o.interested ? "Interested" : "I'm interested"}
              </Button>
              {o.contact && o.interested && <a href={o.contact.includes("@") ? `mailto:${o.contact}` : `tel:${o.contact}`} className="flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-navy hover:bg-slate-50"><Mail className="h-3.5 w-3.5" /> Contact</a>}
              {isAdmin && <Button variant="outline" size="sm" onClick={() => verify(o)}>{o.medlync_verified ? "Unverify" : "Verify"}</Button>}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Posted by {o.posted_by_name} · {timeAgo(o.created_at)}</p>
          </div>
        ))}
      </div>

      <CreateOppDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
